import React from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { I18n } from '../../../../Locales/I18nService';
import { useUndoRedoMap } from '../../../../Plugins/FloorPlan/Context/UndoRedoMapContext';
import { useMapContext } from '../../../../Plugins/FloorPlan/Context/MapContext';
import { SingleChoiceDialog } from '../../../Pages/Projects/Components/ChoiceDialog/SingleChoiceDialog';
import { Logger } from '../../../../Errors/Logger';
import { LogLevelType } from '../../../../Services/Analytics/LogLevelType';
import { DebugContextButton } from 'Web/Pages/Quote/Widgets/Debug/DebugContextButton';
import { styled } from '@material-ui/core';

export const MAP_ERROR_DIALOG_ID = 'map-error-fallback-dialog';

const fallbackRender = ({ error, resetErrorBoundary }: FallbackProps) => {
    return (
        <SingleChoiceDialog
            id={MAP_ERROR_DIALOG_ID}
            title={I18n.get('MapErrorFallbackDialog_Title')}
            body={I18n.get('MapErrorFallbackDialog_Subtitle')}
            labelChoice={I18n.get('COMMON_OK')}
            enableCloseOutside={false}
            onClickChoice={() => resetErrorBoundary()}
            styles={{ content: { paddingBottom: 24 } }}>
            <MapError error={error} />
        </SingleChoiceDialog>
    );
};

const LOG_MSG = 'MAP_ERROR_BOUNDARY_PREVENT_WSOD';

export const MapErrorBoundary = ({ children }: React.PropsWithChildren<{}>) => {
    const map = useMapContext();
    const undoMap = useUndoRedoMap();

    return (
        <ErrorBoundary
            fallbackRender={fallbackRender}
            onReset={() => {
                const undoState = undoMap.undo();
                if (undoState) {
                    map.update({
                        values: undoState,
                        mapModified: true,
                        logs: { event: 'undo map state from map error boundary' },
                    });
                }
            }}
            onError={(error) => {
                Logger.logAnalytics({
                    message: LOG_MSG,
                    level: LogLevelType.Error,
                    value: error,
                });
            }}>
            {children}
        </ErrorBoundary>
    );
};

export const Error = styled('div')({
    marginTop: 16,
    maxHeight: 500,
    overflow: 'auto',
    color: 'red',
});

export const MapError = (props: Partial<FallbackProps>) => {
    const debugEnabled = Boolean(localStorage.getItem(Logger.LoggerId));
    const { message, stack } = props.error || {};
    const errorData = { message, stack };
    return (
        <>
            <DebugContextButton data={errorData} style={{ top: 24, left: 24 }} />
            <Error>{debugEnabled && JSON.stringify(errorData, null, 2)}</Error>
        </>
    );
};
